import { useRef, useState } from "react"
import styled from "styled-components"

import { Text } from "@minuthq/meatball-ui-react"
import { endOfDay, startOfDay, subDays } from "date-fns"
import { zonedTimeToUtc } from "date-fns-tz"
import { useResizeObserver } from "usehooks-ts"

import { NoiseInsightGraphCard } from "src/components/Reports/NoiseInsights/NoiseInsightGraphCard"
import { TimezoneSelect } from "src/components/Reports/TimezoneSelect"
import { usePostDashboardNoiseInsightsViewAnalyticsClicked } from "src/data/analytics/queries/dashboardNoiseInsightsAnalyticsQueries"
import { useFetchNoiseEventsByDate } from "src/data/noiseInsights/queries/noiseInsightQueries"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { TTimezone } from "src/data/timezones/timezones"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { InternalLink } from "src/ui/Link/InternalLink"
import { spacing } from "src/ui/spacing"
import { getStoredSelectedTimezone } from "src/utils/l10n"

const MOBILE_BREAKPOINT = 500

export function DashboardNoiseInsight() {
  const { orgId } = useOrganization()
  const { t, langKeys } = useTranslate()

  const postDashboardNoiseInsightsViewAnalyticsClicked =
    usePostDashboardNoiseInsightsViewAnalyticsClicked()

  const startDate = startOfDay(subDays(new Date(), 30))
  const endDate = endOfDay(new Date())

  const [timezone, setTimezone] = useState<TTimezone>(
    getStoredSelectedTimezone().timezone
  )

  const filters = {
    from: zonedTimeToUtc(startDate, timezone.tzCode).toISOString(),
    to: zonedTimeToUtc(endDate, timezone.tzCode).toISOString(),
  }

  const viewRef = useRef<HTMLDivElement>(null)
  const { width = 0 } = useResizeObserver({
    ref: viewRef,
    box: "border-box",
  })

  const resolution = width < MOBILE_BREAKPOINT ? "week" : "day"
  // Fetch events day-by-day/week-by-week to pass to the graph card
  const fetchEventsByDate = useFetchNoiseEventsByDate({
    orgId,
    filters: {
      ...filters,
      resolution: resolution,
    },
  })

  return (
    <div ref={viewRef}>
      <Text variant="heading2" marginBottom={spacing.L}>
        {t(langKeys.dashboard_noise_insight_title)}
      </Text>
      <NoiseInsightGraphCard
        data={fetchEventsByDate.data}
        isLoading={fetchEventsByDate.isLoading}
        startDate={startDate}
        endDate={endDate}
        info={
          <InternalLink
            onClick={() => {
              postDashboardNoiseInsightsViewAnalyticsClicked.mutate()
            }}
            to={Routes.NoiseInsights.location()}
          >
            {t(langKeys.dashboard_noise_insight_link)}
          </InternalLink>
        }
        timeZoneContent={
          <TimezoneContent>
            <TimeZoneTitle>
              <Text variant="body">{t(langKeys.timezone)}: </Text>
            </TimeZoneTitle>
            <TimezoneSelect timezone={timezone} onChange={setTimezone} />
          </TimezoneContent>
        }
      />
    </div>
  )
}

const TimezoneContent = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${spacing.S};
`
const TimeZoneTitle = styled.div`
  margin-right: ${spacing.S};
`
